import {isSafeLinkUrl} from './linkUrl';
import {getStoragePathFromDownloadUrl} from './storagePaths';
import {httpsCallable} from 'firebase/functions';
import {functions} from './firebase';

export const FAVICON_IMAGE_PREFIX = 'favicon:';

export const isFaviconImageRef = (image) => (
	typeof image === 'string' && image.startsWith(FAVICON_IMAGE_PREFIX)
);

export const getFaviconImageUrl = (image) => (
	isFaviconImageRef(image) ? image.slice(FAVICON_IMAGE_PREFIX.length) : null
);

export const createFaviconImageRef = (url) => `${FAVICON_IMAGE_PREFIX}${url}`;

export const getLinkImageDisplayUrl = (image) => {
	if (!image) return null;
	const faviconUrl = getFaviconImageUrl(image);
	if (faviconUrl) return faviconUrl;
	return typeof image === 'string' ? image : null;
};

export const isFirebaseStorageImageUrl = (url) => {
	if (typeof url !== 'string' || !url) return false;
	if (url.startsWith('/')) return true;
	return Boolean(getStoragePathFromDownloadUrl(url));
};

const ICON_REL_PATTERN = /(^|\s)(?:shortcut\s+)?icon|apple-touch-icon(?:-precomposed)?(\s|$)/i;

export const extractIconUrlsFromHtml = (html, baseUrl) => {
	if (typeof html !== 'string' || !html) return [];

	const urls = [];
	const linkTags = html.match(/<link\b[^>]*>/gi) ?? [];

	linkTags.forEach((tag) => {
		const rel = tag.match(/\brel=["']([^"']+)["']/i)?.[1] ?? '';
		if (!ICON_REL_PATTERN.test(rel)) return;

		const href = tag.match(/\bhref=["']([^"']+)["']/i)?.[1];
		if (!href || href.startsWith('data:')) return;

		try {
			urls.push(new URL(href, baseUrl).href);
		} catch {
			// ignore invalid href values
		}
	});

	return urls;
};

const THIRD_PARTY_FAVICON_HOSTS = new Set([
	'www.google.com',
	'icons.duckduckgo.com',
]);

export const getHostnameFromUrl = (url) => {
	try {
		return new URL(url).hostname;
	} catch {
		return null;
	}
};

export const getThirdPartyFaviconFetchUrls = (urlOrHostname) => {
	const hostname = typeof urlOrHostname === 'string' && urlOrHostname.includes('/')
		? getHostnameFromUrl(urlOrHostname)
		: urlOrHostname;
	if (!hostname) return [];
	return [
		`https://www.google.com/s2/favicons?domain=${hostname}&sz=128`,
		`https://icons.duckduckgo.com/ip3/${hostname}.ico`,
	];
};

export const isThirdPartyFaviconFetchUrl = (url) => {
	try {
		return THIRD_PARTY_FAVICON_HOSTS.has(new URL(url).hostname);
	} catch {
		return false;
	}
};

export const getFaviconCandidatesForLinkUrl = (linkUrl) => {
	if (!isSafeLinkUrl(linkUrl)) return [];

	try {
		const parsed = new URL(linkUrl.trim());
		const {origin, hostname} = parsed;
		return [
			`${origin}/favicon.ico`,
			`${origin}/favicon.png`,
			`${origin}/apple-touch-icon.png`,
			`${origin}/apple-touch-icon-precomposed.png`,
			...getThirdPartyFaviconFetchUrls(hostname),
		];
	} catch {
		return [];
	}
};

const buildFaviconCandidateList = (linkUrl, {
	preferredUrls = [],
	htmlCandidates = [],
} = {}) => {
	const allCandidates = getFaviconCandidatesForLinkUrl(linkUrl);
	const thirdPartyCount = getThirdPartyFaviconFetchUrls(
		getHostnameFromUrl(linkUrl) ?? ''
	).length;
	const sameOriginCandidates = allCandidates.slice(0, -thirdPartyCount);
	const thirdPartyCandidates = allCandidates.slice(-thirdPartyCount);

	return uniqueUrls([
		...preferredUrls,
		...htmlCandidates,
		...sameOriginCandidates,
		...thirdPartyCandidates,
	]);
};

export const getFaviconUrlFromLinkUrl = (linkUrl) => (
	getFaviconCandidatesForLinkUrl(linkUrl)[0] ?? null
);

export const probeImageUrl = (url) => new Promise((resolve) => {
	if (!url) {
		resolve(false);
		return;
	}
	const img = new Image();
	img.onload = () => resolve(true);
	img.onerror = () => resolve(false);
	img.src = url;
});

export const resolveFaviconUrlsFromPageHtml = async (linkUrl) => {
	if (!isSafeLinkUrl(linkUrl)) return [];

	try {
		const response = await fetch(linkUrl.trim(), {
			mode: 'cors',
			credentials: 'omit',
			redirect: 'follow',
		});
		if (!response.ok) return [];
		const html = await response.text();
		return extractIconUrlsFromHtml(html, linkUrl);
	} catch {
		return [];
	}
};

const uniqueUrls = (urls) => {
	const seen = new Set();
	return urls.filter((url) => {
		if (!url || seen.has(url)) return false;
		seen.add(url);
		return true;
	});
};

export const resolveFaviconForLinkUrl = async (linkUrl, {
	preferredUrls = [],
	resolveFromPageHtml = false,
} = {}) => {
	if (!isSafeLinkUrl(linkUrl)) return null;

	const htmlCandidates = resolveFromPageHtml
		? await resolveFaviconUrlsFromPageHtml(linkUrl)
		: [];

	const candidates = buildFaviconCandidateList(linkUrl, {
		preferredUrls,
		htmlCandidates,
	});

	for (const url of candidates) {
		if (await probeImageUrl(url)) {
			return url;
		}
	}

	return null;
};

export const FAVICON_FETCH_STRATEGY = {
	FULL: 'full',
	CALLABLE_ONLY: 'callable-only',
};

export const resolveLinkImageForForm = async (linkUrl) => {
	if (!isSafeLinkUrl(linkUrl)) return null;
	return resolveLinkImageViaCallable(linkUrl);
};

const inferFileName = (url, mimeType) => {
	try {
		const pathname = new URL(url).pathname;
		const baseName = pathname.split('/').pop();
		if (baseName && /\.[a-z0-9]+$/i.test(baseName)) {
			return baseName;
		}
	} catch {
		// fall through
	}
	if (mimeType === 'image/jpeg') return 'favicon.jpg';
	if (mimeType === 'image/png') return 'favicon.png';
	return 'favicon.ico';
};

const canvasToPngFile = (img, fileName) => new Promise((resolve) => {
	const canvas = document.createElement('canvas');
	const width = Math.max(1, img.naturalWidth || img.width || 1);
	const height = Math.max(1, img.naturalHeight || img.height || 1);
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext('2d');
	if (!context) {
		resolve(null);
		return;
	}
	context.drawImage(img, 0, 0, width, height);
	canvas.toBlob((blob) => {
		if (!blob) {
			resolve(null);
			return;
		}
		resolve(new File([blob], fileName.replace(/\.[^.]+$/, '.png'), {type: 'image/png'}));
	}, 'image/png');
});

const loadImageWithCors = (url) => new Promise((resolve, reject) => {
	const img = new Image();
	img.crossOrigin = 'anonymous';
	img.onload = () => resolve(img);
	img.onerror = () => reject(new Error('Could not load image.'));
	img.src = url;
});

const fetchImageBlob = async (url) => {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`Failed to fetch image (${response.status})`);
	}
	return response.blob();
};

const blobToFaviconFile = async (blob, sourceUrl) => {
	const mimeType = blob.type || 'image/png';
	if (mimeType === 'image/jpeg' || mimeType === 'image/png') {
		return new File([blob], inferFileName(sourceUrl, mimeType), {type: mimeType});
	}
	const img = await loadImageWithFile(blob);
	return await canvasToPngFile(img, inferFileName(sourceUrl, mimeType));
};

const base64ToFile = (base64, mimeType, fileName) => {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i += 1) {
		bytes[i] = binary.charCodeAt(i);
	}
	return new File([bytes], fileName, {type: mimeType});
};

const SUPPORTED_FAVICON_FILE_TYPES = new Set(['image/jpeg', 'image/png']);

const normalizeFaviconFile = async (file) => {
	if (!file) return null;
	if (SUPPORTED_FAVICON_FILE_TYPES.has(file.type)) return file;
	try {
		const img = await loadImageWithFile(file);
		return await canvasToPngFile(img, file.name);
	} catch {
		return null;
	}
};

const callableResultToFile = async (data) => {
	if (!data.ok || !data.data || !data.mimeType) return null;
	const file = base64ToFile(data.data, data.mimeType, data.fileName ?? 'favicon.png');
	return normalizeFaviconFile(file);
};

const fetchImageViaCallable = async (imageUrl, linkUrl) => {
	if (!linkUrl || !isSafeLinkUrl(linkUrl)) return null;

	try {
		const callable = httpsCallable(functions, 'fetchLinkFavicon');
		const result = await callable({imageUrl, linkUrl});
		return callableResultToFile(result?.data ?? {});
	} catch {
		return null;
	}
};

const resolveLinkImageViaCallable = async (linkUrl) => {
	if (!isSafeLinkUrl(linkUrl)) return null;

	try {
		const callable = httpsCallable(functions, 'resolveLinkFavicon');
		const result = await callable({linkUrl});
		return callableResultToFile(result?.data ?? {});
	} catch {
		return null;
	}
};

export const fetchImageUrlAsFile = async (url, options = {}) => {
	if (!url) return null;

	const normalizedOptions = typeof options === 'string'
		? {preferredName: options}
		: options;
	const {linkUrl, fetchStrategy = FAVICON_FETCH_STRATEGY.FULL} = normalizedOptions;

	if (fetchStrategy === FAVICON_FETCH_STRATEGY.CALLABLE_ONLY) {
		const callableFile = await fetchImageViaCallable(url, linkUrl);
		if (callableFile) return callableFile;
		return resolveLinkImageViaCallable(linkUrl);
	}

	const fetchUrls = uniqueUrls([
		...(isThirdPartyFaviconFetchUrl(url) ? [url] : getThirdPartyFaviconFetchUrls(url)),
		url,
	]);

	for (const fetchUrl of fetchUrls) {
		try {
			const blob = await fetchImageBlob(fetchUrl);
			const file = await blobToFaviconFile(blob, fetchUrl);
			if (file) return file;
		} catch {
			// try next source
		}
	}

	for (const fetchUrl of fetchUrls) {
		const callableFile = await fetchImageViaCallable(fetchUrl, linkUrl);
		if (callableFile) return callableFile;
	}

	for (const fetchUrl of fetchUrls) {
		try {
			const img = await loadImageWithCors(fetchUrl);
			const file = await canvasToPngFile(img, inferFileName(fetchUrl, 'image/png'));
			if (file) return file;
		} catch {
			// try next source
		}
	}

	return null;
};

const loadImageWithFile = (blob) => new Promise((resolve, reject) => {
	const objectUrl = URL.createObjectURL(blob);
	const img = new Image();
	img.onload = () => {
		URL.revokeObjectURL(objectUrl);
		resolve(img);
	};
	img.onerror = () => {
		URL.revokeObjectURL(objectUrl);
		reject(new Error('Could not read image blob.'));
	};
	img.src = objectUrl;
});
